import React from 'react';
import { Shield, CheckCircle, AlertTriangle, Phone } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function Safety() {
  const tips = [
    { icon: '✅', title: 'Check Reviews First', desc: 'Always read ratings and reviews from previous customers before booking a provider.' },
    { icon: '📞', title: 'Communicate Clearly', desc: 'Agree on the full scope, price, and timeline before work begins. Keep records of your conversations.' },
    { icon: '🏠', title: 'Meet in Safe Spaces', desc: 'For first-time providers, meet in a common area first before inviting them into your home.' },
    { icon: '💳', title: 'Agree on Payment Terms', desc: 'Confirm payment method upfront. Never pay large sums before work is completed.' },
    { icon: '📷', title: 'Document Everything', desc: 'Take photos before and after the job. This protects both you and the provider.' },
    { icon: '🚨', title: 'Report Issues', desc: 'If something feels wrong, report it to us immediately via Contact. We take all concerns seriously.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Navigation />

      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-4">Safety on HIVE</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Your safety is our top priority. Here's how we keep the HIVE community safe — and how you can protect yourself too.
          </p>
        </div>
      </section>

      {/* What HIVE Does */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl border border-green-200/50 dark:border-green-700/50 p-8 mb-12">
            <h2 className="text-xl font-black text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" /> What HIVE Does to Keep You Safe
            </h2>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
              {['Profile verification for all providers', 'Public review and rating system', 'Ability to report suspicious accounts', 'Prompt response to safety concerns', 'Community guidelines enforcement'].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>

          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-8">Safety Tips for Customers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {tips.map((tip, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6">
                <div className="text-3xl mb-3">{tip.icon}</div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{tip.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tip.desc}</p>
              </div>
            ))}
          </div>

          {/* Emergency */}
          <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200/50 dark:border-red-700/50 p-8 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">In an Emergency</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">If you feel unsafe or are in danger, contact emergency services immediately.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:119" className="px-6 py-2 bg-red-500 text-white rounded-xl font-bold text-sm hover:bg-red-600 transition-all flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" /> Call 119 (Police)
              </a>
              <button onClick={() => window.location.href = '/contact'} className="px-6 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl font-bold text-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 transition-all">
                Report to HIVE
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}